/** The persisted document: page metadata, the Puck block tree, and the asset registry. */

export type AssetId = string;

export type DocType = 'landing' | 'article';

/**
 * Which market a page targets. Drives hreflang and, critically, the `.ukdownload` /
 * `.usdownload` class pair that the live site's geo switcher toggles. Get this wrong and
 * app-store badges silently vanish for half the audience.
 */
export type Geo = 'uk' | 'us' | 'both';

export interface AssetRef {
  id: AssetId;
  url: string;
  /** Mandatory. Emitted as width/height attributes so the browser reserves space (CLS). */
  width: number;
  height: number;
  /** Warned above 200_000 — the SEO guide's limit for blog images. */
  bytes: number;
  alt: string;
  /** Descriptive filename, e.g. `teacher-gift-ideas.jpg`, never `IMG_4523.jpg`. */
  filename: string;
  source?: {
    provider: 'unsplash' | 'pexels' | 'pixabay' | 'upload';
    author: string;
    authorUrl: string;
    /** Unsplash requires firing this when a photo is actually used, not when it is searched. */
    downloadLocation?: string;
  };
}

export interface PageMeta {
  primaryKeyword: string;
  secondaryKeywords: string[];

  /** Validated `/^[a-z0-9-]+$/`, keyword present, no years, no filler words. */
  slug: string;

  /** Max 55 chars AND max ~530px at Arial 20px. Both, because they catch different failures. */
  metaTitle: string;
  /** Max 155 chars. */
  metaDescription: string;

  /** Site-relative, e.g. `/lp/collect-money-for-a-leaving-gift`. Becomes an absolute og:url. */
  canonicalPath: string;

  ogImage?: AssetId;
  /** Always `en_GB` — easy to get wrong, and the live site omits it entirely. */
  ogLocale: 'en_GB';
  /** Derived from docType. Fixes the live site's `website` on article pages. */
  ogType: 'website' | 'article';

  /** ISO 8601 only. The live site emits "Sep 11, 2026", which Google does not parse. */
  publishedAt?: string;
  modifiedAt?: string;

  /** Resolves to `/blog-authors/<slug>` — the live JSON-LD wrongly says `/team-members/`. */
  authorSlug?: string;

  jsonLd: {
    organization: boolean;
    breadcrumb: boolean;
    article: boolean;
    webPage: boolean;
  };

  analytics: {
    /** Becomes `mixpanel.track('Page View', { Name: ... })`. */
    mixpanelPageName: string;
    /** Default `utm_content` for CTAs that don't override it. */
    utmContentDefault: string;
  };

  geo: Geo;
}

/**
 * Puck's `Data` shape, typed loosely on purpose.
 *
 * Puck is pre-1.0. Mirroring its generic `Data<Config>` type here would couple every package
 * to its release cycle, which is exactly what the dependency boundary exists to prevent.
 * The editor narrows this; nothing else needs to.
 */
export interface PuckDataSnapshot {
  root: { props?: Record<string, unknown> };
  content: PuckItem[];
  zones?: Record<string, PuckItem[]>;
}

export interface PuckItem {
  type: string;
  props: Record<string, unknown> & { id: string };
}

export interface CollctivDoc {
  /** Bump when the shape changes and add a migration. Never load a doc without migrating. */
  schemaVersion: 1;
  id: string;
  title: string;
  docType: DocType;
  meta: PageMeta;
  puck: PuckDataSnapshot;
  assets: Record<AssetId, AssetRef>;
  createdAt: string;
  updatedAt: string;
}

export function emptyMeta(docType: DocType): PageMeta {
  return {
    primaryKeyword: '',
    secondaryKeywords: [],
    slug: '',
    metaTitle: '',
    metaDescription: '',
    canonicalPath: '',
    ogLocale: 'en_GB',
    ogType: docType === 'article' ? 'article' : 'website',
    jsonLd: {
      organization: true,
      breadcrumb: true,
      article: docType === 'article',
      webPage: docType === 'landing',
    },
    analytics: { mixpanelPageName: '', utmContentDefault: '' },
    geo: 'uk',
  };
}
